import { useCallback, useEffect, useRef, useState } from "react";
import { useAtrasado } from "../componentes/atraso.js";
import { CabecalhoDePagina } from "../componentes/CabecalhoDePagina.js";
import { Link } from "react-router";
import {
  abaixoDaMateria,
  agruparPorMateria,
  dataCurta,
  plural,
  type ModeloProvaEntrada,
  type ModeloProvaNaLista,
  type CategoriaResumo,
  type EstudoAtual,
  type LinhaDaComposicao,
  type PainelDoSimulado,
  type QuestaoRevisada,
  type RelatorioDeSimulado,
} from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import { CirculoMao } from "../componentes/Tracos.js";
import {
  IconeAcerto,
  IconeCriar,
  IconeErro,
  IconeSimulado,
} from "../componentes/icones.js";
import { MenuDeAcoes } from "../componentes/Controles.js";
import { Metrica } from "../componentes/Metrica.js";
import { Executar } from "./Executar.js";

/**
 * Simulado (SPEC §6, tela 7).
 *
 * Quatro telas em uma, decididas pelo estado do servidor — o mesmo desenho de
 * `Estudar`, pela mesma razão: o cursor mora no banco, então abrir esta rota em
 * qualquer aparelho cai onde a prova parou.
 *
 *  · prova aberta            → a execução, com cronômetro;
 *  · relatório pedido        → o resultado;
 *  · sem modelo de prova     → o estado vazio, com a ação de criar;
 *  · com modelos             → a lista, o histórico e o editor.
 *
 * A execução reusa `Executar` inteiro. Não há motor de prova separado: o que
 * muda entre estudar e provar é o cronômetro, o botão de entregar e o SM-2 não
 * ser tocado — e esse último já é decisão do servidor (RN-34), não da tela.
 */

type Estado =
  | { nome: "carregando" }
  | { nome: "pronto"; painel: PainelDoSimulado }
  | { nome: "provando"; painel: PainelDoSimulado; atual: EstudoAtual }
  | { nome: "relatorio"; relatorio: RelatorioDeSimulado }
  | { nome: "erro"; mensagem: string };

export function Simulado() {
  const [estado, setEstado] = useState<Estado>({ nome: "carregando" });
  const mostrarEsqueleto = useAtrasado(estado.nome === "carregando");
  const [categorias, setCategorias] = useState<CategoriaResumo[]>([]);
  const [editando, setEditando] = useState<ModeloProvaNaLista | "nova" | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setEstado({ nome: "carregando" });
    try {
      const painel = await pedir("simulado.painel");
      if (painel.aberto) {
        setEstado({ nome: "provando", painel, atual: await pedir("estudo.ver") });
      } else {
        setEstado({ nome: "pronto", painel });
      }
    } catch (e) {
      setEstado({
        nome: "erro",
        mensagem: e instanceof FalhaDaApi ? e.message : "Não foi possível carregar os simulados.",
      });
    }
  }, []);

  useEffect(() => {
    void carregar();
    void pedir("categoria.listar").then((r) => setCategorias(r.categorias)).catch(() => setCategorias([]));
  }, [carregar]);

  const entregar = useCallback(
    async (simuladoId: string) => {
      try {
        setEstado({ nome: "relatorio", relatorio: await pedir("simulado.encerrar", { params: { id: simuladoId } }) });
      } catch (e) {
        setAviso(e instanceof FalhaDaApi ? e.message : "Não foi possível entregar a prova.");
      }
    },
    [],
  );

  if (estado.nome === "carregando") {
    return (
      <Moldura>
        <div aria-busy="true" data-esqueleto={mostrarEsqueleto ? "visivel" : "mudo"}>
          <span className="so-leitor" role="status">
            Carregando os simulados
          </span>
          <div className="esqueleto esqueleto--faixa" />
          <div className="esqueleto esqueleto--bloco" />
        </div>
      </Moldura>
    );
  }

  if (estado.nome === "erro") {
    return (
      <Moldura>
        <section className="estado estado--erro" role="alert">
          <h2 className="estado__titulo">Não deu para carregar</h2>
          <p className="estado__texto">{estado.mensagem}</p>
          <button className="botao" type="button" onClick={() => void carregar()}>
            Tentar de novo
          </button>
        </section>
      </Moldura>
    );
  }

  if (estado.nome === "relatorio") {
    return (
      <Moldura>
        <Relatorio r={estado.relatorio} aoVoltar={() => void carregar()} />
      </Moldura>
    );
  }

  if (estado.nome === "provando") {
    const aberto = estado.painel.aberto!;
    return (
      <div className="tela">
        <div className="tela__corpo tela__corpo--prova">
          {/*
            O relógio mora na barra de ações da execução (RN-55), e não num
            cartão à parte: dois relógios na mesma tela competem, e o cartão
            duplicava o progresso que a barra já mostra. O que sobrou aqui é a
            entrega automática — comportamento, não desenho.
          */}
          <EntregaNoPrazo prazoEm={aberto.prazoEm} aoEstourar={() => void entregar(aberto.simuladoId)} />
          <p className="etiqueta tinta-suave">
            {aberto.modeloProvaNome} · {aberto.questoes} questões
          </p>
          {aviso && (
            <p className="estado__texto" role="alert">
              {aviso}
            </p>
          )}
          <Executar
            atual={estado.atual}
            aoMudar={(novo) => setEstado({ ...estado, atual: novo })}
            modo="simulado"
            prazoEm={aberto.prazoEm}
            aoEncerrar={() => void entregar(aberto.simuladoId)}
          />
        </div>
      </div>
    );
  }

  const { modelosProva, historico } = estado.painel;

  if (editando !== null) {
    return (
      <div className="simulado">
        <EditorDeModeloProva
          inicial={editando === "nova" ? null : editando}
          categorias={categorias}
          aoFechar={() => setEditando(null)}
          aoSalvar={() => {
            setEditando(null);
            void carregar();
          }}
        />
      </div>
    );
  }

  const feitasPorModelo = new Map<string, number>();
  for (const h of historico) feitasPorModelo.set(h.modeloProvaNome, (feitasPorModelo.get(h.modeloProvaNome) ?? 0) + 1);

  return (
    <Moldura
      acoes={
        <button className="botao" type="button" onClick={() => setEditando("nova")}>
          <Icone glifo={IconeCriar} papel="acao" />
          Novo modelo de prova
        </button>
      }
    >
      {aviso && (
        <p className="campo__erro" role="alert">
          {aviso}
        </p>
      )}

      <div className="simulado__grade">
        <div className="simulado__principal">
          <section aria-labelledby="modelos-titulo">
            <h2 className="t-secao" id="modelos-titulo">
              Modelos de prova
            </h2>
            {modelosProva.length === 0 ? (
              <div className="estado">
                <p className="estado__texto">
                  Nenhum modelo ainda. O modelo é a receita da prova: questões por categoria, quanto cada uma vale, o
                  tempo e a nota de corte.
                </p>
                <button className="botao botao--secundario" type="button" onClick={() => setEditando("nova")}>
                  <Icone glifo={IconeCriar} papel="acao" />
                  Criar modelo de prova
                </button>
              </div>
            ) : (
              <ul className="modelos-mao">
                {modelosProva.map((b) => (
                  <CartaoDeModeloProva
                    key={b.id}
                    b={b}
                    feitas={feitasPorModelo.get(b.nome) ?? 0}
                    aoEditar={() => setEditando(b)}
                    aoExcluir={async () => {
                      try {
                        await pedir("modeloProva.excluir", { params: { id: b.id } });
                        void carregar();
                      } catch (e) {
                        setAviso(e instanceof FalhaDaApi ? e.message : "Não foi possível excluir.");
                      }
                    }}
                    aoComecar={async () => {
                      setAviso(null);
                      try {
                        const m = await pedir("simulado.montar", { params: { id: b.id } });
                        if (m.faltou.length > 0) {
                          setAviso(
                            `A prova saiu com ${m.questoes} questões: o acervo não tinha o bastante em ${m.faltou.length} categoria(s). A pontuação máxima acompanha o que foi sorteado.`,
                          );
                        }
                        void carregar();
                      } catch (e) {
                        setAviso(e instanceof FalhaDaApi ? e.message : "Não foi possível montar a prova.");
                      }
                    }}
                  />
                ))}
              </ul>
            )}
          </section>

          <Historico
            linhas={historico}
            aoAbrir={async (id) => {
              try {
                setEstado({ nome: "relatorio", relatorio: await pedir("simulado.relatorio", { params: { id } }) });
              } catch (e) {
                setAviso(e instanceof FalhaDaApi ? e.message : "Não foi possível abrir o relatório.");
              }
            }}
          />
        </div>

        <aside className="postit simulado__postit">
          <p className="postit__titulo">O que é um modelo</p>
          <p className="postit__texto">
            É a receita da prova: quantas questões de cada categoria, quanto cada uma vale, quanto tempo e a nota de
            corte. A pontuação máxima e o corte saem dele.
          </p>
          <p className="postit__texto">Durante a prova não aparece certo ou errado. O resultado vem na entrega.</p>
        </aside>
      </div>
    </Moldura>
  );
}

function Moldura({ children, acoes }: { children: React.ReactNode; acoes?: React.ReactNode }) {
  return (
    <div className="simulado">
      <CabecalhoDePagina
        titulo="Simulado"
        subtitulo="Uma prova inteira, no tempo dela, sem retorno item a item."
        acoes={acoes}
      />
      <div className="linha-vazia" />
      {children}
    </div>
  );
}

/* ---------------------------------------------------------------- cronômetro */

/**
 * A entrega automática quando o tempo acaba (RN-55).
 *
 * Não desenha nada: o relógio visível é o da barra de ações da execução. O que
 * mora aqui é só o comportamento — a prova se entrega sozinha uma vez, e o
 * `ref` é o que impede o tique seguinte de entregar de novo.
 *
 * O prazo vem do servidor e é contado contra ele: relógio de navegador atrasado
 * ou adiantado não pode dar nem tirar tempo de prova.
 */
function EntregaNoPrazo({ prazoEm, aoEstourar }: { prazoEm: string; aoEstourar: () => void }) {
  const jaEstourou = useRef(false);

  useEffect(() => {
    const tique = () => {
      if (Date.parse(prazoEm) - Date.now() <= 0 && !jaEstourou.current) {
        jaEstourou.current = true;
        aoEstourar();
      }
    };
    tique();
    const id = setInterval(tique, 1000);
    return () => clearInterval(id);
  }, [prazoEm, aoEstourar]);

  // O aviso dos cinco minutos continua existindo para quem usa leitor de tela:
  // o relógio da barra tem `aria-live` desligado de propósito.
  return (
    <span className="so-leitor" role="status">
      {Date.parse(prazoEm) - Date.now() < 5 * 60_000 ? "Menos de cinco minutos." : ""}
    </span>
  );
}

/* ------------------------------------------------------------- lista e cartão */

function CartaoDeModeloProva({
  b,
  feitas,
  aoComecar,
  aoEditar,
  aoExcluir,
}: {
  b: ModeloProvaNaLista;
  feitas: number;
  aoComecar: () => void;
  aoEditar: () => void;
  aoExcluir: () => void;
}) {
  const faltando = b.composicaoResolvida.filter((l) => l.disponiveis < l.quantidade);
  const horas = Math.floor(b.duracaoMinutos / 60);
  const minutos = b.duracaoMinutos % 60;
  const tempo = horas > 0 ? `${horas} h${minutos > 0 ? ` ${minutos} min` : ""}` : `${minutos} min`;
  const corte =
    b.notaCorte === null
      ? "sem corte"
      : `corte ${Math.ceil((Number(b.notaCorte) / 100) * Number(b.pontuacaoMaxima))} de ${Number(b.pontuacaoMaxima)} pontos`;

  return (
    <li className="modelo-mao">
      <h3 className="t-secao">{b.nome}</h3>
      <p className="t-ajuda">
        {b.totalDeQuestoes} questões · {plural(b.composicaoResolvida.length, "categoria")} · {tempo} · {corte}
      </p>
      {!b.disponibilidade_hoje.liberado && (
        <p className="t-ajuda" role="status">
          {b.disponibilidade_hoje.motivo}
          {b.disponibilidade_hoje.liberaEm && ` Volta em ${dataCurta(b.disponibilidade_hoje.liberaEm)}.`}
        </p>
      )}
      {faltando.length > 0 && b.disponibilidade_hoje.liberado && (
        <p className="t-ajuda">
          {faltando.length} categoria(s) sem questões suficientes: a prova sai menor, e a pontuação máxima acompanha.
        </p>
      )}
      <div className="modelo-mao__acoes">
        <button className="link-mao" type="button" disabled={!b.disponibilidade_hoje.liberado} onClick={aoComecar}>
          <Icone glifo="play" papel="acao" tamanho="p" />
          começar prova
        </button>
        <button className="link-mao link-mao--tinta" type="button" onClick={aoEditar}>
          editar
        </button>
        <span className="t-anotacao-p">{feitas === 0 ? "nenhuma prova feita" : plural(feitas, "prova feita", "provas feitas")}</span>
        <MenuDeAcoes
          rotulo={`Mais ações de ${b.nome}`}
          acoes={[
            {
              rotulo: "Excluir modelo",
              destrutiva: true,
              confirmacao: `Excluir o modelo de prova "${b.nome}"? As provas já feitas continuam no histórico.`,
              aoEscolher: aoExcluir,
            },
          ]}
        />
      </div>
    </li>
  );
}

function Historico({ linhas, aoAbrir }: { linhas: PainelDoSimulado["historico"]; aoAbrir: (id: string) => void }) {
  // A primeira prova terminada de cada modelo é a linha de base.
  const base = new Map<string, number>();
  for (const l of [...linhas].reverse()) {
    if (l.finalizado && l.pontuacao !== null && !base.has(l.modeloProvaNome)) base.set(l.modeloProvaNome, Number(l.pontuacao));
  }
  const ehBase = (l: PainelDoSimulado["historico"][number]) =>
    [...linhas].reverse().find((x) => x.finalizado && x.modeloProvaNome === l.modeloProvaNome)?.simuladoId === l.simuladoId;

  return (
    <section className="historico-simulado" aria-labelledby="hist-titulo">
      <div className="historico-simulado__topo">
        <h2 className="t-secao" id="hist-titulo">
          Histórico
        </h2>
        <p className="t-anotacao-p">a primeira de cada modelo vira a linha de base</p>
      </div>
      {linhas.length === 0 ? (
        <p className="t-ajuda">Nenhuma prova feita ainda.</p>
      ) : (
        <table className="tabela-mao">
          <thead>
            <tr>
              <th scope="col">data</th>
              <th scope="col">modelo</th>
              <th scope="col">nota</th>
              <th scope="col">resultado</th>
              <th scope="col">vs. base</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              const b = base.get(l.modeloProvaNome);
              const dif = l.finalizado && l.pontuacao !== null && b !== undefined ? Number(l.pontuacao) - b : null;
              return (
                <tr key={l.simuladoId}>
                  <td className="tabela-mao__mao">{dataCurta(l.dia).slice(0, 5)}</td>
                  <td>
                    <button className="tabela-mao__link" type="button" onClick={() => aoAbrir(l.simuladoId)}>
                      {l.modeloProvaNome}
                    </button>
                  </td>
                  <td className="tabela-mao__mao tabela-mao__forte">
                    {l.finalizado ? `${Number(l.pontuacao)}/${Number(l.pontuacaoMaxima)}` : "em andamento"}
                  </td>
                  <td className={l.passou === null ? "tabela-mao__mao" : l.passou ? "tabela-mao__mao t-verde tabela-mao__forte" : "tabela-mao__mao t-vermelho tabela-mao__forte"}>
                    {!l.finalizado ? "" : l.passou === null ? "sem corte" : l.passou ? "passou" : "abaixo do corte"}
                  </td>
                  <td className="tabela-mao__mao">
                    {!l.finalizado ? "" : ehBase(l) ? "base" : dif === null ? "" : `${dif > 0 ? "+" : ""}${dif}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * O relatório. É ele que dá sentido a fazer a prova: um número final sozinho
 * diz que você foi mal, e não onde. A quebra por categoria vem ordenada pelo
 * que mais custou pontos, que é a resposta para "por onde eu começo".
 */
function Relatorio({ r, aoVoltar }: { r: RelatorioDeSimulado; aoVoltar: () => void }) {
  const encolheu = r.pontuacaoMaxima !== r.pontuacaoMaximaPrevista;
  const pior = r.porCategoria.filter((l) => l.pontos !== l.pontosPossiveis);

  return (
    <>
      <section className="cartao" aria-labelledby="res-titulo">
        <div className="linha linha--espalhada">
          <h2 className="etiqueta" id="res-titulo">
            {r.modeloProvaNome}
          </h2>
          <span className="etiqueta tinta-suave">{dataCurta(r.dia)}</span>
        </div>

        <p className="relatorio__nota dado">
          {r.pontuacao}
          <span className="relatorio__de"> / {r.pontuacaoMaxima}</span>
        </p>
        <p className="etiqueta tinta-suave">{r.percentual}% da prova</p>

        {r.passou !== null && (
          <p className={r.passou ? "veredito veredito--bom" : "veredito veredito--ruim"} role="status">
            {r.passou ? "Passou" : "Não passou"} — o corte era {r.corte} ponto(s), {r.cortePercentual}% de{" "}
            {r.pontuacaoMaxima}.
          </p>
        )}
        {r.passou === null && (
          <p className="etiqueta tinta-suave">
            Este modelo de prova não tem nota de corte, então não há veredito — só a pontuação.
          </p>
        )}

        <div className="faixa-metricas">
          <Metrica rotulo="Questões" valor={String(r.questoes)} nota={`${r.respondidas} respondidas`} />
          <Metrica glifo={IconeAcerto} rotulo="Acertos" valor={String(r.acertos)} nota={`de ${r.questoes}`} />
          <Metrica
            rotulo="Tempo"
            valor={r.duracaoMs === null ? "—" : `${Math.round(r.duracaoMs / 60_000)} min`}
            nota={`de ${r.duracaoMinutos}`}
          />
          <Metrica rotulo="Máxima" valor={r.pontuacaoMaxima} nota="do que foi sorteado" />
        </div>

        {r.estourouOTempo && (
          <p className="etiqueta tinta-suave" role="status">
            O tempo acabou antes da entrega. As questões em branco contam como erro — foram sorteadas e não deram
            ponto.
          </p>
        )}

        {encolheu && (
          <p className="etiqueta tinta-suave">
            O modelo de prova pedia {r.pontuacaoMaximaPrevista} ponto(s), mas o acervo não tinha questão suficiente em
            alguma categoria. A prova valeu {r.pontuacaoMaxima}, e o corte foi derivado desse valor: você não perde
            ponto por questão que não foi perguntada.
          </p>
        )}
      </section>

      <section className="cartao" aria-labelledby="cat-titulo">
        <h2 className="etiqueta" id="cat-titulo">
          Onde os pontos foram
        </h2>
        <p className="etiqueta tinta-suave">
          Ordenado pelo que mais custou, não pelo que tem pior taxa: uma categoria de peso alto perde a prova mesmo
          acertando metade.
        </p>

        <ul className="categorias">
          {r.porCategoria.map((l) => {
            const perdidos = (Number(l.pontosPossiveis) - Number(l.pontos)).toFixed(2).replace(/\.?0+$/, "") || "0";
            const fracao = Number(l.pontosPossiveis) === 0 ? 0 : Number(l.pontos) / Number(l.pontosPossiveis);
            return (
              <li className="categoria" key={l.categoriaId}>
                <div className="linha linha--espalhada">
                  <span className="categoria__nome">{l.caminho.join(" › ") || l.categoriaId}</span>
                  <span className="categoria__taxa dado">
                    {l.pontos} / {l.pontosPossiveis}
                  </span>
                </div>
                <div
                  className="categoria__barra"
                  style={{ "--fracao": fracao } as React.CSSProperties}
                  role="img"
                  aria-label={`${l.acertos} acertos em ${l.sorteadas} questões, ${l.pontos} de ${l.pontosPossiveis} pontos`}
                >
                  <span className="categoria__barra-cheia" />
                </div>
                <p className="etiqueta tinta-suave">
                  {l.acertos} de {l.sorteadas} · peso {l.peso} · perdeu {perdidos} ponto(s)
                  {l.sorteadas < l.previstas && ` · o modelo pedia ${l.previstas}`}
                </p>
              </li>
            );
          })}
        </ul>

        {pior.length === 0 && (
          <p className="etiqueta tinta-suave">Prova inteira. Não há categoria a recuperar desta vez.</p>
        )}
      </section>

      {/* Tabela paralela: o gráfico de barras é decoração, os números são o dado. */}
      <table className="so-leitor">
        <caption>Pontuação por categoria</caption>
        <thead>
          <tr>
            <th scope="col">Categoria</th>
            <th scope="col">Acertos</th>
            <th scope="col">Questões</th>
            <th scope="col">Peso</th>
            <th scope="col">Pontos</th>
            <th scope="col">Possíveis</th>
          </tr>
        </thead>
        <tbody>
          {r.porCategoria.map((l) => (
            <tr key={l.categoriaId}>
              <th scope="row">{l.caminho.join(" › ") || l.categoriaId}</th>
              <td>{l.acertos}</td>
              <td>{l.sorteadas}</td>
              <td>{l.peso}</td>
              <td>{l.pontos}</td>
              <td>{l.pontosPossiveis}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/*
        **A correção da prova, questão a questão** (RN-34, ADR 021).

        Durante o simulado não há gabarito nenhum na tela; ele sai aqui. Sem
        esta seção, tirar a revelação da execução não teria mudado a correção de
        lugar — teria apagado ela.
      */}
      {r.itens.length > 0 && (
        <section className="cartao pilha" aria-labelledby="rev-titulo">
          <h2 className="etiqueta" id="rev-titulo">
            Questão a questão
          </h2>
          <p className="etiqueta tinta-suave">
            Na ordem em que caíram. O que você marcou e o que era.
          </p>

          <ol className="revisao">
            {r.itens.map((q) => (
              <QuestaoDaRevisao key={q.itemId} q={q} />
            ))}
          </ol>
        </section>
      )}

      <div className="linha">
        <button className="botao" type="button" onClick={aoVoltar}>
          Voltar aos simulados
        </button>
        <Link className="botao botao--secundario" to="/estudar">
          Estudar as erradas
        </Link>
      </div>
      <p className="etiqueta tinta-suave">
        As questões que você errou já entraram na fila de erradas. O calendário de revisão não foi mexido: prova
        não reagenda (RN-34).
      </p>
    </>
  );
}

/**
 * Uma questão na revisão da prova.
 *
 * Três estados, e eles não são dois: **acertou**, **errou** e **em branco**. A
 * terceira não é um caso da segunda — deixar em branco conta como erro na
 * pontuação (RN-34), mas na revisão ela diz outra coisa: faltou tempo, não
 * faltou conhecimento. Juntar as duas apagaria a diferença justamente para quem
 * está tentando descobrir por que perdeu ponto.
 *
 * **Item sem certo objetivo não recebe rótulo vazio** — mesma decisão da RN-46.
 * No cartão quem julga é o usuário, e "(sem gabarito)" leria como defeito de
 * importação.
 */
function QuestaoDaRevisao({ q }: { q: QuestaoRevisada }) {
  const marcada =
    q.marcado?.tipo === "multipla"
      ? q.marcado.alternativa
      : q.marcado?.tipo === "certo_errado"
        ? q.marcado.valor
          ? "Certo"
          : "Errado"
        : null;
  const g = q.gabarito as { tipo?: string; correta?: string | boolean } | null;
  const certa =
    g?.tipo === "multipla"
      ? String(g.correta)
      : g?.tipo === "certo_errado"
        ? g.correta
          ? "Certo"
          : "Errado"
        : null;

  const estado = !q.respondida ? "branco" : q.acertou ? "certa" : "errada";

  return (
    <li className={`revisao__questao revisao__questao--${estado}`}>
      <div className="linha linha--espalhada">
        <span className="etiqueta">
          <span className="dado">{q.numero}</span> · {q.categoria.join(" › ")} · peso {q.peso}
        </span>
        <span className="etiqueta">
          {estado === "branco" ? (
            "Em branco"
          ) : (
            <>
              <Icone glifo={q.acertou ? IconeAcerto : IconeErro} papel="estado" tamanho="p" />
              {q.acertou ? "Acertou" : "Errou"}
            </>
          )}
        </span>
      </div>

      <p className="leitura revisao__enunciado">{q.enunciado}</p>

      {(marcada !== null || certa !== null) && (
        <p className="etiqueta">
          {marcada !== null && (
            <>
              Você marcou <span className="dado">{String(marcada)}</span>
              {q.alternativas && typeof marcada === "string" && q.alternativas[marcada]
                ? ` — ${q.alternativas[marcada]}`
                : ""}
            </>
          )}
          {marcada !== null && certa !== null && " · "}
          {certa !== null && (
            <>
              Gabarito <span className="dado">{String(certa)}</span>
              {q.alternativas && typeof certa === "string" && q.alternativas[certa]
                ? ` — ${q.alternativas[certa]}`
                : ""}
            </>
          )}
        </p>
      )}

      {q.explicacao && <p className="leitura--apoio">{q.explicacao}</p>}
    </li>
  );
}

/* -------------------------------------------------------------- editor */

/**
 * O editor do modelo de prova. A pontuação máxima e o corte em pontos aparecem
 * enquanto se digita, calculados pelas **mesmas funções** que o servidor usa —
 * é o que impede a tela de mostrar um número e a prova valer outro.
 */
function EditorDeModeloProva({
  inicial,
  categorias,
  aoFechar,
  aoSalvar,
}: {
  inicial: ModeloProvaNaLista | null;
  categorias: CategoriaResumo[];
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [nome, setNome] = useState(inicial?.nome ?? "");
  const [duracao, setDuracao] = useState(String(inicial?.duracaoMinutos ?? 60));
  const [corte, setCorte] = useState(inicial?.notaCorte ?? "");
  const [modo, setModo] = useState<"livre" | "dia_da_semana" | "cadencia">(
    inicial?.disponibilidade.modo ?? "livre",
  );
  const [cadaDias, setCadaDias] = useState(
    inicial?.disponibilidade.modo === "cadencia" ? String(inicial.disponibilidade.cadaDias) : "7",
  );
  const [dias, setDias] = useState<number[]>(
    inicial?.disponibilidade.modo === "dia_da_semana" ? inicial.disponibilidade.dias : [6],
  );
  const [linhas, setLinhas] = useState<LinhaDaComposicao[]>(inicial?.composicao ?? []);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const maxima = linhas.reduce((s, l) => s + Number(l.peso || 0) * l.quantidade, 0);
  const corteEmPontos = corte === "" ? null : (maxima * Number(corte)) / 100;

  const salvar = async () => {
    setErro(null);
    setSalvando(true);
    try {
      const entrada: ModeloProvaEntrada = {
        nome: nome.trim(),
        composicao: linhas,
        duracaoMinutos: Number(duracao),
        notaCorte: corte.trim() === "" ? null : corte.trim(),
        disponibilidade:
          modo === "livre"
            ? { modo: "livre" }
            : modo === "cadencia"
              ? { modo: "cadencia", cadaDias: Number(cadaDias) }
              : { modo: "dia_da_semana", dias },
      };
      if (inicial) await pedir("modeloProva.editar", { params: { id: inicial.id }, corpo: entrada });
      else await pedir("modeloProva.criar", { corpo: entrada });
      aoSalvar();
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  };

  const usadas = new Set(linhas.map((l) => l.categoriaId));
  const livres = categorias.filter((c) => !usadas.has(c.id));

  const horas = Math.floor(Number(duracao || 0) / 60);
  const minutos = Number(duracao || 0) % 60;
  const questoes = linhas.reduce((n, l) => n + l.quantidade, 0);
  const minPorQuestao = questoes > 0 ? Math.round(Number(duracao || 0) / questoes) : null;
  const fmt = (n: number) => n.toFixed(2).replace(/\.?0+$/, "") || "0";
  const mudarLinha = (i: number, parcial: Partial<LinhaDaComposicao>) =>
    setLinhas(linhas.map((x, j) => (j === i ? { ...x, ...parcial } : x)));

  return (
    <section className="modelo-editor" aria-labelledby="editor-titulo">
      <button type="button" className="link-mao link-mao--tinta" onClick={aoFechar}>
        <Icone glifo="chevron_left" papel="acao" tamanho="p" />
        voltar ao simulado
      </button>
      <h1 className="t-titulo" id="editor-titulo">
        {inicial ? "Editar modelo de prova" : "Novo modelo de prova"}
      </h1>
      <p className="t-ajuda">A pontuação máxima e o corte saem daqui; você não digita nenhum dos dois.</p>

      {erro && (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      )}

      <div className="modelo-editor__campos">
        <div className="campo">
          <label className="campo__rotulo" htmlFor="modelo-nome">
            Nome
          </label>
          <input
            id="modelo-nome"
            className="campo__entrada"
            placeholder="ex.: P3 de FMC II"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
        </div>
        <fieldset className="campo campo--grupo">
          <legend className="campo__rotulo">Tempo</legend>
          <span className="campo-com-sufixo">
            <input
              className="campo__entrada campo__entrada--numero"
              type="number"
              min={0}
              aria-label="Horas"
              value={horas}
              onChange={(e) => setDuracao(String(Math.max(0, Number(e.target.value) || 0) * 60 + minutos))}
            />
            <span className="t-anotacao t-2">h</span>
            <input
              className="campo__entrada campo__entrada--numero"
              type="number"
              min={0}
              max={59}
              aria-label="Minutos"
              value={String(minutos).padStart(2, "0")}
              onChange={(e) => setDuracao(String(horas * 60 + Math.min(59, Math.max(0, Number(e.target.value) || 0))))}
            />
            <span className="t-anotacao t-2">min</span>
          </span>
        </fieldset>
        <div className="campo">
          <label className="campo__rotulo" htmlFor="modelo-corte">
            Nota de corte
          </label>
          <span className="campo-com-sufixo">
            <input
              id="modelo-corte"
              className="campo__entrada campo__entrada--numero"
              type="number"
              min={0}
              max={100}
              placeholder="–"
              value={corte}
              onChange={(e) => setCorte(e.target.value)}
            />
            <span className="t-anotacao t-2">% dos pontos</span>
          </span>
        </div>
      </div>

      <div className="modelo-editor__topo-tabela">
        <h2 className="t-secao">Questões por categoria</h2>
        <p className="t-anotacao-p">sugeridas a partir das matérias da trilha</p>
      </div>
      <table className="tabela-mao modelo-editor__tabela">
        <thead>
          <tr>
            <th scope="col">categoria</th>
            <th scope="col">disponíveis</th>
            <th scope="col">questões</th>
            <th scope="col">vale</th>
            <th scope="col">pontos</th>
            <th scope="col">
              <span className="so-leitor">Tirar</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => {
            const cat = categorias.find((c) => c.id === l.categoriaId);
            const disp = inicial?.disponiveisPorCategoria[l.categoriaId];
            const nomeCat = cat ? cat.caminho.join(", ") : l.categoriaId;
            return (
              <tr key={l.categoriaId}>
                <td className="modelo-editor__nome">{nomeCat}</td>
                <td className="tabela-mao__mao t-2">{disp ?? "–"}</td>
                <td>
                  <Passo
                    rotulo={`Questões de ${nomeCat}`}
                    valor={String(l.quantidade)}
                    menos={() => mudarLinha(i, { quantidade: Math.max(1, l.quantidade - 1) })}
                    mais={() => mudarLinha(i, { quantidade: l.quantidade + 1 })}
                  />
                </td>
                <td>
                  <Passo
                    rotulo={`Peso de cada questão de ${nomeCat}`}
                    valor={`${fmt(Number(l.peso || 0))} pt`}
                    menos={() => mudarLinha(i, { peso: fmt(Math.max(0.5, Number(l.peso || 0) - 0.5)) })}
                    mais={() => mudarLinha(i, { peso: fmt(Number(l.peso || 0) + 0.5) })}
                  />
                </td>
                <td className="tabela-mao__mao tabela-mao__forte">{fmt(Number(l.peso || 0) * l.quantidade)}</td>
                <td>
                  <button
                    className="botao-icone"
                    type="button"
                    onClick={() => setLinhas(linhas.filter((_, j) => j !== i))}
                    aria-label={`Tirar ${cat?.nome ?? l.categoriaId} da composição`}
                  >
                    <Icone glifo="x" papel="acao" />
                  </button>
                </td>
                {disp !== undefined && l.quantidade > disp && (
                  <td className="modelo-editor__aviso" colSpan={6}>
                    O acervo só tem {disp} nesta categoria.
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {livres.length > 0 && (
        <label className="modelo-editor__somar">
          <span className="link-mao">
            <Icone glifo="plus" papel="acao" tamanho="p" />
            categoria
          </span>
          <select
            className="modelo-editor__select"
            value=""
            aria-label="Somar categoria"
            onChange={(e) => {
              if (!e.target.value) return;
              setLinhas([...linhas, { categoriaId: e.target.value, quantidade: 10, peso: "1" }]);
            }}
          >
            <option value="">Escolha uma…</option>
            {agruparPorMateria(livres, (c) => c.caminho).map((g) => (
              <optgroup key={g.materia ?? "(sem matéria)"} label={g.materia ?? "(sem matéria)"}>
                {g.itens.map((c) => (
                  <option key={c.id} value={c.id}>
                    {abaixoDaMateria(c.caminho).join(" › ") || c.nome}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      )}

      <div className="cartao-mao modelo-editor__resumo" aria-live="polite">
        <p>
          <span className="t-numero">{questoes}</span> <span className="t-anotacao t-2">questões</span>
        </p>
        <p>
          <span className="t-numero">{fmt(maxima)}</span> <span className="t-anotacao t-2">pontos</span>
        </p>
        <p>
          <span className="t-numero">{corteEmPontos === null ? "–" : Math.ceil(corteEmPontos)}</span>{" "}
          <span className="t-anotacao t-2">de corte</span>
        </p>
        <p>
          <span className="t-numero">{minPorQuestao ?? "–"}</span> <span className="t-anotacao t-2">min por questão</span>
        </p>
      </div>

      <fieldset className="campo campo--grupo modelo-editor__quando">
        <legend className="campo__rotulo">Quando pode ser feito</legend>
        <div className="fila-mao" role="group" aria-label="Disponibilidade">
          {(["livre", "dia_da_semana", "cadencia"] as const).map((m) => (
            <button key={m} type="button" className="chip-mao" aria-pressed={m === modo} onClick={() => setModo(m)}>
              {m === "livre" ? "sempre" : m === "dia_da_semana" ? "em dias da semana" : "a cada N dias"}
            </button>
          ))}
        </div>
        {modo === "cadencia" && (
          <span className="campo-com-sufixo">
            <span className="t-anotacao t-2">a cada</span>
            <input
              className="campo__entrada campo__entrada--numero"
              type="number"
              min={1}
              aria-label="A cada quantos dias"
              value={cadaDias}
              onChange={(e) => setCadaDias(e.target.value)}
            />
            <span className="t-anotacao t-2">dias</span>
          </span>
        )}
        {modo === "dia_da_semana" && (
          <div className="dias-mao" role="group" aria-label="Dias da semana">
            {["dom", "seg", "ter", "qua", "qui", "sex", "sáb"].map((nomeDia, i) => (
              <button
                key={nomeDia}
                type="button"
                className={dias.includes(i) ? "dia-mao dia-mao--marcado" : "dia-mao"}
                aria-pressed={dias.includes(i)}
                onClick={() => setDias(dias.includes(i) ? dias.filter((d) => d !== i) : [...dias, i])}
              >
                {nomeDia}
                <CirculoMao feito={dias.includes(i)} />
              </button>
            ))}
          </div>
        )}
      </fieldset>

      <div className="modelo-editor__acoes">
        <button
          className="botao"
          type="button"
          disabled={salvando || linhas.length === 0 || nome.trim() === ""}
          onClick={() => void salvar()}
        >
          <Icone glifo="check" papel="acao" />
          {salvando ? "Salvando…" : "Salvar modelo"}
        </button>
        <button className="link-mao link-mao--tinta" type="button" onClick={aoFechar}>
          cancelar
        </button>
      </div>
    </section>
  );
}

/** − valor + com os passos em círculo à mão (9h). */
function Passo({ rotulo, valor, menos, mais }: { rotulo: string; valor: string; menos: () => void; mais: () => void }) {
  return (
    <span className="passo" role="group" aria-label={rotulo}>
      <button type="button" className="contador__passo" aria-label={`Diminuir: ${rotulo}`} onClick={menos}>
        <Icone glifo="menos" papel="acao" />
      </button>
      <span className="passo__valor">{valor}</span>
      <button type="button" className="contador__passo" aria-label={`Aumentar: ${rotulo}`} onClick={mais}>
        <Icone glifo="plus" papel="acao" />
      </button>
    </span>
  );
}
